import React from "react";
import { classNames } from "components/utils";
import { Button } from "components/Graphs/Button";
import * as TooltipPrimitives from "@radix-ui/react-tooltip";

interface CloudDisplayProps {
  provider?: string;
  cloud?: string;
  region?: string;
  className?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  tooltip?: string;
}

const CloudDisplay: React.FC<CloudDisplayProps> = ({
  provider = "Unknown Provider",
  cloud,
  className,
  icon,
  onClick,
  tooltip,
}) => {
  const isClickable = !!onClick;

  const baseClasses = classNames(
    "flex items-center",
    "text-sm",
    "",
    isClickable
      ? "cursor-pointer rounded-md transition-colors hover:text-highlight"
      : "",
    className || "",
  );

  const content = (
    <div className="flex flex-row items-center gap-1">
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <p className="text-sm leading-[10px]">
        {provider}
        {cloud ? <span className="text-[8px] ml-1">({cloud})</span> : ""}
      </p>
    </div>
  );

  const wrapped = (children: React.ReactNode) => {
    if (!tooltip) return <>{children}</>;
    return (
      <TooltipPrimitives.Provider>
        <TooltipPrimitives.Root delayDuration={0}>
          <TooltipPrimitives.Trigger asChild>
            {children as React.ReactElement}
          </TooltipPrimitives.Trigger>
          <TooltipPrimitives.Portal>
            <TooltipPrimitives.Content
              side="top"
              sideOffset={4}
              className="z-50 max-w-xs rounded-md bg-zinc-800 px-3 py-2 text-xs text-white shadow-md"
            >
              {tooltip}
              <TooltipPrimitives.Arrow className="fill-zinc-800" />
            </TooltipPrimitives.Content>
          </TooltipPrimitives.Portal>
        </TooltipPrimitives.Root>
      </TooltipPrimitives.Provider>
    );
  };

  if (isClickable) {
    return wrapped(
      <Button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onClick?.();
        }}
        className={baseClasses}
        type="button"
        variant="noshow"
      >
        {content}
      </Button>,
    );
  }

  return wrapped(<div className={baseClasses}>{content}</div>);
};

export default CloudDisplay;
